import { AssessmentAnswer } from '../../generated/graphql';
import { getAnswerValue, getAnswerValues } from './answerHelpers';
import {
  flattenQuestionsForForm,
  ProgramAssessmentQuestion,
} from './program-assessment';

export interface DisplayQuestion {
  question: ProgramAssessmentQuestion | null;
  questionText: string;
  isFromAnswer: boolean;
  matchedAnswer?: AssessmentAnswer | null;
  questionIndex?: number;
}

/**
 * Extracts the question from an assessment answer.
 * This preserves the exact question that was presented to the patient
 * when they completed the assessment, even if the template has been updated.
 */
function getQuestionFromAnswer(answer: AssessmentAnswer): string | null {
  // Type guard to check if the answer has a question property
  if ('question' in answer) {
    const answerWithQuestion = answer as any;
    if (typeof answerWithQuestion.question === 'string') {
      return answerWithQuestion.question;
    }
  }

  // Handle UnsupportedAssessmentAnswer which has unsupportedQuestion instead
  if ('unsupportedQuestion' in answer) {
    const answerWithUnsupportedQuestion = answer as any;
    if (typeof answerWithUnsupportedQuestion.unsupportedQuestion === 'string') {
      return answerWithUnsupportedQuestion.unsupportedQuestion;
    }
  }

  return null;
}

function getAnswerContentKey(answer: AssessmentAnswer): string {
  const multiSelectValues = getAnswerValues(answer);
  if (multiSelectValues.length > 0) {
    return multiSelectValues.join('\u0001');
  }
  return getAnswerValue(answer) ?? '';
}

function isOrphanAlreadyDisplayed(
  displayQuestions: DisplayQuestion[],
  questionText: string,
  answer: AssessmentAnswer,
): boolean {
  const contentKey = getAnswerContentKey(answer);
  return displayQuestions.some(
    (item) =>
      item.questionText === questionText &&
      item.matchedAnswer != null &&
      getAnswerContentKey(item.matchedAnswer) === contentKey,
  );
}

export function createQuestionsForDisplay(
  questions: ProgramAssessmentQuestion[] | undefined,
  answers: AssessmentAnswer[] | undefined,
): DisplayQuestion[] {
  // Answers are stored in the same order as flattenQuestionsForForm produces —
  // matching the form submission path — so pair by index, not question text.
  const flattenedQuestions = flattenQuestionsForForm(questions ?? []);
  const questionsByText = buildQuestionLookupMap(flattenedQuestions);

  const displayQuestions = processTemplateQuestions(
    flattenedQuestions,
    answers,
  );
  addOrphanedQuestions(displayQuestions, questionsByText, answers);

  return displayQuestions;
}

function buildQuestionLookupMap(
  questions: ProgramAssessmentQuestion[] | undefined,
): Map<string, ProgramAssessmentQuestion> {
  const lookupMap = new Map<string, ProgramAssessmentQuestion>();
  questions?.forEach((question) => {
    lookupMap.set(question.question, question);
  });
  return lookupMap;
}

function processTemplateQuestions(
  questions: ProgramAssessmentQuestion[] | undefined,
  answers: AssessmentAnswer[] | undefined,
): DisplayQuestion[] {
  const displayQuestions: DisplayQuestion[] = [];

  questions?.forEach((templateQuestion, index) => {
    const matchingAnswer = answers?.[index] ?? null;
    const answeredQuestion = matchingAnswer
      ? getQuestionFromAnswer(matchingAnswer)
      : null;

    displayQuestions.push({
      question: templateQuestion,
      questionText: answeredQuestion || templateQuestion.question,
      isFromAnswer: Boolean(matchingAnswer),
      matchedAnswer: matchingAnswer,
      questionIndex: index,
    });
  });

  return displayQuestions;
}

function addOrphanedQuestions(
  displayQuestions: DisplayQuestion[],
  questionsByText: Map<string, ProgramAssessmentQuestion>,
  answers: AssessmentAnswer[] | undefined,
): void {
  const matchedIndices = new Set(
    displayQuestions
      .map((item) => item.questionIndex)
      .filter((index): index is number => index !== undefined),
  );

  answers?.forEach((answer, index) => {
    if (matchedIndices.has(index)) {
      return;
    }

    const question = getQuestionFromAnswer(answer);
    if (!question) {
      return;
    }

    // When the form template was updated after submission, stored answers can be
    // longer than the current template. Skip tail answers whose prompt text and
    // answer content are already rendered so duplicate rows are not appended
    // again after the signature block. Orphans with the same prompt but
    // different content are kept.
    if (isOrphanAlreadyDisplayed(displayQuestions, question, answer)) {
      return;
    }

    const templateQuestion = questionsByText.get(question);
    displayQuestions.push({
      question: templateQuestion || null,
      questionText: question,
      isFromAnswer: true,
      matchedAnswer: answer,
    });
  });
}
